import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, X, CheckSquare, Square, Loader2 } from "lucide-react";
import client from "@/lib/api";
import { useCosts } from "@/context/CostsContext";
import { MetricInfo } from "@/components/MetricInfo";

function scoreColor(score) {
    if (score >= 70) return "border-success/40 text-success";
    if (score >= 40) return "border-warning/50 text-warning";
    return "border-destructive/50 text-destructive";
}

/**
 * <KeywordResearchModal /> — Keyword Magic Tool equivalent.
 * Search a seed keyword/topic → real DataForSEO Labs keyword ideas,
 * scored high -> low by opportunity, tick + add in one click.
 */
export function KeywordResearchModal({ open, onClose, projectId, existingKeywords, limit, tier, onSuccess }) {
    const { costs } = useCosts();
    const [seed, setSeed] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [creditBalance, setCreditBalance] = useState(null);

    useEffect(() => {
        if (!open) return;
        setSeed(""); setResults([]); setSelected(new Set()); setSearched(false);
        client.get("/usage/me").then((r) => {
            setCreditBalance({
                total_available: r.data?.credits?.total_available ?? 0,
                is_admin: !!r.data?.credits?.is_admin,
            });
        }).catch(() => setCreditBalance(null));
    }, [open]);

    const existingSet = useMemo(
        () => new Set((existingKeywords || []).map((k) => k.toLowerCase())),
        [existingKeywords],
    );
    const rowCount = (existingKeywords || []).length;
    const room = Math.max(0, limit - rowCount);

    const search = async () => {
        if (!seed.trim()) { toast.error("Enter a seed keyword or topic."); return; }
        setSearching(true);
        try {
            const r = await client.post(`/projects/${projectId}/keywords/research`, { seed: seed.trim(), limit: 50 });
            const rows = (r.data.keywords || []).map((k) => ({ ...k, already_tracked: existingSet.has(k.keyword.toLowerCase()) || k.already_tracked }));
            setResults(rows);
            setSearched(true);
            setSelected(new Set(rows.filter((k) => !k.already_tracked).slice(0, 10).map((k) => k.keyword)));
            toast.success(`Found ${rows.length} keyword idea${rows.length === 1 ? "" : "s"} for "${seed.trim()}"`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Keyword research failed");
        } finally {
            setSearching(false);
        }
    };

    const toggle = (kw) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(kw)) next.delete(kw); else next.add(kw);
        return next;
    });

    const selectableCount = results.filter((r) => !r.already_tracked).length;
    const allSelected = selectableCount > 0 && selected.size === selectableCount;
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(results.filter((r) => !r.already_tracked).map((r) => r.keyword)));

    const willInsert = Math.min(selected.size, room);
    const totalCost = willInsert; // keyword_add = 1 credit each, charged by /keywords/batch
    const availableCredits = creditBalance?.total_available ?? null;
    const isAdmin = !!creditBalance?.is_admin;
    const insufficientCredits = !isAdmin && availableCredits !== null && totalCost > availableCredits;

    const addSelected = async () => {
        if (!selected.size) { toast.error("Select at least one keyword to add."); return; }
        if (room === 0) { toast.error(`Plan limit reached (${rowCount}/${limit}). Upgrade to add more.`); return; }
        if (insufficientCredits) { toast.error(`Not enough credits: need ${totalCost}, have ${availableCredits}.`); return; }
        setSubmitting(true);
        try {
            const items = results.filter((r) => selected.has(r.keyword)).map((r) => ({ keyword: r.keyword, intent: r.intent || null }));
            const r = await client.post(`/projects/${projectId}/keywords/batch`, { keywords: items });
            const { added, skipped_duplicates, skipped_over_limit, keywords } = r.data;
            onSuccess?.(keywords);
            const parts = [`Added ${added} keyword${added === 1 ? "" : "s"}`];
            if (skipped_duplicates) parts.push(`skipped ${skipped_duplicates} duplicate${skipped_duplicates === 1 ? "" : "s"}`);
            if (skipped_over_limit) parts.push(`${skipped_over_limit} over plan limit`);
            toast.success(parts.join(" · "));
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add selected keywords.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;
    const researchCost = costs?.keyword_research ?? 2;

    return (
        <div
            data-testid="keyword-research-overlay"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div data-testid="keyword-research-modal" className="bg-card border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <div>
                        <div className="overline">Keyword research</div>
                        <h2 className="font-display text-2xl tracking-tight mt-1">Find new keyword ideas</h2>
                        <p className="text-xs text-muted-foreground mt-1">Real DataForSEO Labs data — scored by demand vs. difficulty, best opportunities first.</p>
                    </div>
                    <button data-testid="keyword-research-close" onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="flex gap-2">
                        <input
                            data-testid="keyword-research-seed-input"
                            value={seed}
                            onChange={(e) => setSeed(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
                            placeholder="Seed keyword or topic — e.g. 'seo audit tool'"
                            className="flex-1 bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary"
                        />
                        <button
                            data-testid="keyword-research-search-btn"
                            onClick={search}
                            disabled={searching}
                            className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50 shrink-0"
                        >
                            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                            Search
                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider bg-primary-foreground/15 border border-primary-foreground/30 px-1.5 py-0.5">
                                {researchCost} cr
                            </span>
                        </button>
                    </div>

                    {searched && results.length === 0 && (
                        <div data-testid="keyword-research-empty" className="text-sm text-muted-foreground text-center py-8">
                            No keyword ideas found for that seed — try a broader topic.
                        </div>
                    )}

                    {results.length > 0 && (
                        <>
                            <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                                <button data-testid="keyword-research-select-all" onClick={toggleAll} className="inline-flex items-center gap-1.5 hover:text-foreground">
                                    {allSelected ? <CheckSquare className="w-3.5 h-3.5 text-primary" /> : <Square className="w-3.5 h-3.5" />}
                                    Select all new ({selectableCount})
                                </button>
                                <span data-testid="keyword-research-selected-count">{selected.size} selected</span>
                            </div>
                            <div className="border border-border max-h-80 overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="text-muted-foreground sticky top-0 bg-card">
                                        <tr className="border-b border-border">
                                            <th className="p-2 w-8"></th>
                                            <th className="text-left p-2">Keyword</th>
                                            <th className="text-left p-2">Volume</th>
                                            <th className="text-left p-2">KD</th>
                                            <th className="text-left p-2">CPC</th>
                                            <th className="text-left p-2">Intent</th>
                                            <th className="text-left p-2">
                                                <span className="inline-flex items-center gap-1">Opportunity <MetricInfo metricKey="keyword_opportunity_score" /></span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {results.map((r) => (
                                            <tr
                                                key={r.keyword}
                                                data-testid={`keyword-research-row-${r.keyword.replace(/\s+/g, "-")}`}
                                                className={`border-b border-border/50 ${r.already_tracked ? "opacity-40" : "hover:bg-accent/20 cursor-pointer"}`}
                                                onClick={() => !r.already_tracked && toggle(r.keyword)}
                                            >
                                                <td className="p-2" onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(r.keyword)}
                                                        disabled={r.already_tracked}
                                                        onChange={() => toggle(r.keyword)}
                                                        data-testid={`keyword-research-check-${r.keyword.replace(/\s+/g, "-")}`}
                                                    />
                                                </td>
                                                <td className="p-2 font-mono text-foreground">
                                                    {r.keyword}
                                                    {r.already_tracked && <span className="ml-2 text-[10px] text-muted-foreground">tracked</span>}
                                                </td>
                                                <td className="p-2 font-mono text-muted-foreground">{(r.volume || 0).toLocaleString()}</td>
                                                <td className="p-2 font-mono text-muted-foreground">{r.difficulty}</td>
                                                <td className="p-2 font-mono text-muted-foreground">${r.cpc}</td>
                                                <td className="p-2 text-muted-foreground">{r.intent || "—"}</td>
                                                <td className="p-2">
                                                    <span className={`inline-block px-1.5 py-0.5 border font-mono ${scoreColor(r.opportunity_score)}`}>{r.opportunity_score}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {creditBalance !== null && (
                                insufficientCredits ? (
                                    <div data-testid="keyword-research-credit-warning" className="border border-destructive/60 bg-destructive/10 p-3 text-xs">
                                        <div className="text-destructive font-mono">Not enough credits — need {totalCost}, you have {availableCredits}.</div>
                                        <Link to="/pricing" className="text-primary underline mt-1 inline-block">Buy a credit pack →</Link>
                                    </div>
                                ) : (
                                    <div data-testid="keyword-research-cost-preview" className="text-[11px] font-mono text-muted-foreground">
                                        Adding {willInsert} keyword{willInsert === 1 ? "" : "s"} will cost {totalCost} credit{totalCost === 1 ? "" : "s"} · Balance: {isAdmin ? "∞ (admin)" : availableCredits}
                                    </div>
                                )
                            )}
                            {selected.size > room && (
                                <div className="text-xs text-warning font-mono">
                                    Only {room} slot{room === 1 ? "" : "s"} left on your {tier} plan — only the first {room} selected will be added.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
                    <button data-testid="keyword-research-cancel" onClick={onClose} className="border border-border px-4 py-2 text-sm hover:border-muted-foreground">
                        Cancel
                    </button>
                    <button
                        data-testid="keyword-research-add-selected"
                        onClick={addSelected}
                        disabled={submitting || selected.size === 0 || insufficientCredits}
                        className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                    >
                        {submitting ? "Adding…" : `Add ${willInsert} selected · ${totalCost} credits`}
                    </button>
                </div>
            </div>
        </div>
    );
}
